import { defineStore, storeToRefs } from 'pinia';
import { fromUnixTime, formatDate, parseDate, isBefore, addDays, de } from '@/resources/helpers';
import { ref } from 'vue';
import { useNuxtApp } from '#imports';
import type { Ref } from 'vue';
import union from "lodash-es/union";
import {
  FILTER_DATE,
  FILTER_SKILL,
  FILTER_HOST,
  FILTER_CITY,
  FILTER_THEME_CATEGORY,
  FILTERS_KEYS,
  ROOT_PATH,
  FILTER_ORDER_BY
} from '@/resources/helpers';
import type { Host, Theme, ThemeCategory } from '@/resources/types';
import { useCategoryStore } from './category';

export const useProductFilterStore = defineStore('productFilter', () => {
  const categoryStore = useCategoryStore();
  const { $i18n, $router } = useNuxtApp();
  const filters: Ref<any> = ref([]);
  const filtersLoading: Ref<boolean> = ref(false);

  const { categories } = storeToRefs(categoryStore);

  const getURLFriendlyFilterKey = (filterKey: string): string => {
    return filterKey.replace('variants_', '');
  };

  const values = FILTERS_KEYS.reduce((map, key) => {
    const isOpen = false;
    let selected: string[] = [];
    let values: string[] = [];
    let count: number = 0
    const value = $router.currentRoute.value.query?.[getURLFriendlyFilterKey(key)] as string;
    if (value) {
      selected = Array.isArray(value) ? value : [value];
      values = Array.isArray(value) ? value : [value];
    }
    map[key] = { isOpen, values, selected, count };
    return map;
  }, {});
  const activeFilters: Ref<any> = ref(values);

  const SKILL_NAMES = {
    1: $i18n.t('category.filters.skill.beginner'),
    2: $i18n.t('category.filters.skill.intermediate'),
    3: $i18n.t('category.filters.skill.advanced')
  };

  const SORT_OPTIONS = {
    name: 'variants_orderBy',
    options: [
      {
        count: null,
        value: `amount&order=DESC`,
        id: 'standard',
        name: $i18n.t('category.filters.sort.standard'),

      },
      {
        count: null,
        value: 'key&order=DESC',
        id: 'DESC',
        name: $i18n.t('category.filters.sort.desc')
      },
      {
        count: null,
        value: 'key&order=ASC',
        id: 'ASC',
        name: $i18n.t('category.filters.sort.asc')
      },
    ]
  }

  async function loadFilters(taxonCode: string) {
    filtersLoading.value = true;
    try {
      const getFilters = await $fetch(`/api/tyk/filters?taxonCode=${taxonCode}`);
      const syliusFilters = [
        {
          name: FILTER_CITY,
          options:
            categories.value?.map(({ name, slug, productCount }) => ({
              id: slug,
              name,
              count: productCount
            })) || []
        },
        ...(getFilters ?? [])
      ];


      filters.value = syliusFilters.reduce((map, obj) => {
        const name = obj.name;
        let options = obj.options;
        if (values[name]) {
          switch (name) {
            case FILTER_THEME_CATEGORY:
              // options = getFilterOptions(options, themeCategories.value, themeCategoryGetters);
              break;
            case FILTER_HOST:
              // options = getFilterOptions(options, hosts.value, hostGetters);
              break;
            case FILTER_DATE:
              options = options.map(({ value }) => ({
                id: formatDate(fromUnixTime(value), 'yyyy-MM-dd', { locale: de })
              }));
              break;
            case FILTER_SKILL:
              options = options
                .filter(({ value }) => SKILL_NAMES[value])
                .map(({ value: id, productCount: count }) => ({
                  id,
                  count,
                  name: SKILL_NAMES[id]
                }));
              break;
          }
          map[name] = { name, options };
        }
        return { ...map, 'variants_orderBy': SORT_OPTIONS };
      }, {});
    } catch (err) {
      console.error('Error in loading filters', err)
    } finally {
      filtersLoading.value = false;
    }

  }

  const getFilterName = (filterKey: string, city: string = '') => {
    switch (filterKey) {
      case FILTER_DATE:
        return $i18n.t('category.filters.filter.date');
      case FILTER_SKILL:
        return $i18n.t('category.filters.filter.skill');
      case 'type':
        return $i18n.t('category.filters.filter.type');
      case FILTER_HOST:
        return $i18n.t('category.filters.filter.host');
      case FILTER_THEME_CATEGORY:
        return $i18n.t('category.filters.filter.theme_category');
      case FILTER_CITY:
        return city && city?.length ? city : $i18n.t('common.city');
      case FILTER_ORDER_BY:
        return $i18n.t('category.filters.filter.sort');

    }
  };

  function getFilterOptions(
    options: Array<any>,
    entityCache: Array<Host | Theme | ThemeCategory>,
    getters: any
  ): Array<Filter> {
    return options
      .filter((f) => {
        return String(f.value.trim()).length > 0;
      })
      .map((f) => {
        const { value, productCount: count } = f;
        let image = '';
        let name = value;
        const entityObject = entityCache?.find(({ uuid }) => uuid === value);
        if (entityObject) {
          name = getters.getName(entityObject);
          image = getters.getImage(entityObject);
        }

        return { id: value, name, count, image };
      });
  }

  const selectFilter = (filterKey: string, value: Array<string>): void => {
    if (Array.isArray(value)) {
      activeFilters.value[filterKey].selected = value;
      return;
    }
    activeFilters.value[filterKey].selected = [];
  };

  const updateFilter = (filterKey: string, value: string, eventCount: number): void => {
    const { values, selected } = activeFilters.value[filterKey];
    if (values.find((v) => v === value) || selected.find((v) => v === value)) {
      activeFilters.value[filterKey].values = values.filter((v) => v !== value);
      activeFilters.value[filterKey].selected = selected.filter((v) => v !== value);
      activeFilters.value[filterKey].count -= eventCount
      return;
    }
    activeFilters.value[filterKey].count += eventCount;
    activeFilters.value[filterKey].values.push(value);
  };

  const resetFilter = (filterKey: string): void => {
    activeFilters.value[filterKey].values = [];
    activeFilters.value[filterKey].selected = [];
    activeFilters.value[filterKey].count = 0;
  };

  const toggleFilter = (filterKey: string): void => {
    for (const key in activeFilters.value) {
      if (key !== filterKey) activeFilters.value[key].isOpen = false;
    }
    if (activeFilters.value[filterKey]) {
      activeFilters.value[filterKey].isOpen = !activeFilters.value[filterKey].isOpen;

    }
  };

  const getURLFilters = (categorySlug: string, urlFilters: any) => {
    const argFilters = {
      taxon: Array.isArray(categorySlug) ? categorySlug?.join(ROOT_PATH)?.replace('-', '_') : null,
      hostUuid: urlFilters.filters?.hostUuid || null,
      themeUuid: urlFilters.filters?.themeUuid || null,
      themeCategoryUuid: urlFilters.filters?.themeCategories_uuid || null,
      skill: urlFilters.filters?.skill?.map((s) => Number.parseInt(s)) || null,
      dates: urlFilters.filters?.eventTimestamp || null,
      orderBy: urlFilters.filters?.orderBy || null,
      codes: ''
    };

    if (String(urlFilters.start).length && String(urlFilters.end).length) {
      let parsedStart = parseDate(urlFilters.start, 'dd.MM.yyyy', new Date());
      const parsedEnd = parseDate(urlFilters.end, 'dd.MM.yyyy', new Date());

      argFilters.dates = [];
      while (isBefore(parsedStart, parsedEnd)) {
        filters.dates.push(formatDate(parsedStart, 'dd.MM.yyyy'));
        parsedStart = addDays(parsedStart, 1);
      }
    }

    Object.keys(argFilters).forEach((filterKey) => {
      const filter = argFilters[filterKey];
      if (Array.isArray(filter) && filter.length === 0) {
        argFilters[filterKey] = null;
      } else if (filter === null || filter === undefined || filter === '') {
        delete argFilters[filterKey];
      }
    });
    return argFilters;
  };

  const uiSelectedFilters = computed(() => Object.keys(activeFilters.value).reduce((acc, key) => {
    const filterValues = activeFilters.value[key].values;
    const filterSelected = activeFilters.value[key].selected;

    acc[getURLFriendlyFilterKey(key)] = [
      ...new Set([...filterValues, ...filterSelected])
    ];
    return acc;
  }, {}));

  const hasSomeFilterValuesSelectedForType = (filterType: string) => {
    const urlSelectedFilters = $router.currentRoute.value?.query;
    if (urlSelectedFilters?.[getURLFriendlyFilterKey(filterType)]?.length > 0 && uiSelectedFilters.value[getURLFriendlyFilterKey(filterType)]?.[0] !== '' || filterType === FILTER_ORDER_BY) {
      return true;
    }

    return uiSelectedFilters.value[getURLFriendlyFilterKey(filterType)]?.length > 0 && uiSelectedFilters.value[getURLFriendlyFilterKey(filterType)]?.[0] !== '';
  };


  return {
    filters,
    activeFilters,
    filtersLoading,
    getFilterName,
    loadFilters,
    getFilterOptions,
    selectFilter,
    updateFilter,
    resetFilter,
    toggleFilter,
    getURLFilters,
    getURLFriendlyFilterKey,
    uiSelectedFilters,
    hasSomeFilterValuesSelectedForType
  };
});
